import { RadioGroup, RadioGroupItem } from '@/global/components/RadioGroup'
import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

type FormRadioGroupProps = {
    name: string
    options: { value: string; label: ReactNode; disabled?: boolean }[]
    label?: ReactNode
    helperText?: ReactNode
    /** Opções lado a lado, como o `row` do `RadioGroup` do MUI. */
    row?: boolean
    defaultValue?: string
    disabled?: boolean
    className?: string
}

/**
 * `RadioGroup` ligado ao react-hook-form. A aparência mora no `RadioGroup` e no
 * `RadioGroupItem`; aqui fica só o `Controller` e a linha auxiliar.
 */
export const FormRadioGroup = ({
    name,
    options,
    label,
    helperText,
    row,
    defaultValue,
    disabled,
    className,
}: FormRadioGroupProps) => {
    const { control } = useFormContext()
    const desabilitado = disabled || (control as any)._options?.disabled

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            render={({ field, fieldState: { error } }) => (
                <div className={cn('flex flex-col gap-1', className)}>
                    {label !== undefined && label !== '' && (
                        <span className="text-sm text-muted-foreground select-none">
                            {label}
                        </span>
                    )}
                    <RadioGroup
                        name={field.name}
                        value={field.value ?? ''}
                        onValueChange={field.onChange}
                        disabled={desabilitado}
                        aria-invalid={!!error || undefined}
                        className={cn(row && 'flex flex-row flex-wrap gap-4')}
                    >
                        {options.map(option => (
                            <RadioGroupItem
                                key={option.value}
                                value={option.value}
                                label={option.label}
                                disabled={option.disabled}
                            />
                        ))}
                    </RadioGroup>
                    {(error?.message || helperText) && (
                        <span
                            className={cn(
                                'px-1 text-xs',
                                error
                                    ? 'text-destructive'
                                    : 'text-muted-foreground',
                            )}
                        >
                            {error?.message ?? helperText}
                        </span>
                    )}
                </div>
            )}
        />
    )
}
